function downloadName(disposition: string, url: URL): string {
    const encoded = /filename\*\s*=\s*UTF-8''([^;]+)/i.exec(disposition)?.[1];
    const plain = /filename\s*=\s*(?:"([^"]*)"|([^;]*))/i.exec(disposition);
    let name = plain?.[1] ?? plain?.[2]?.trim() ?? url.pathname.split('/').pop() ?? '';
    try { name = decodeURIComponent(encoded ?? name); } catch { /* Keep the unencoded name. */ }
    return name.replace(/[\\/\x00-\x1f]/g, '_') || 'download';
}

export type PreviewFileDownloader = (url: string, signal: AbortSignal) => Promise<{ blob: Blob; disposition: string }>;

/** Keep lesson links from navigating, and save readable file responses outside the iframe. */
export function attachPreviewLinkHandler(doc: Document, downloadDocument: Document = document,
    downloadExternal?: PreviewFileDownloader): () => void {
    const controller = new AbortController();
    const pending = new Set<string>();
    const objectUrls = new Map<string, ReturnType<typeof setTimeout>>();

    const download = async (url: URL) => {
        if (pending.has(url.href)) return;
        pending.add(url.href);
        try {
            let blob: Blob;
            let disposition: string;
            let sourceUrl = url.href;
            if (downloadExternal && ['http:', 'https:'].includes(url.protocol)
                && (url.origin !== downloadDocument.location?.origin
                    || /\/protected_storage\.php\/download$/.test(url.pathname))) {
                const file = await downloadExternal(url.href, controller.signal);
                blob = file.blob;
                disposition = file.disposition;
            } else {
                const response = await fetch(url.href, { signal: controller.signal });
                if (!response.ok || controller.signal.aborted) return;
                const type = (response.headers.get('Content-Type') ?? '').split(';')[0].trim().toLowerCase();
                disposition = response.headers.get('Content-Disposition') ?? '';
                const attachment = /^attachment\b/i.test(disposition.trim());
                // A filename or a .pdf suffix is not proof: redirects may return login/error pages.
                if (!attachment && (!type || ['text/html', 'application/xhtml+xml'].includes(type))) {
                    await response.body?.cancel();
                    return;
                }
                blob = await response.blob();
                sourceUrl = response.url || url.href;
            }
            if (controller.signal.aborted) return;
            const objectUrl = URL.createObjectURL(blob);
            // The parent document owns the download, so the sandboxed preview never navigates.
            const link = downloadDocument.createElement('a');
            link.href = objectUrl;
            link.download = downloadName(disposition, new URL(sourceUrl));
            link.hidden = true;
            downloadDocument.body.appendChild(link);
            try { link.click(); } finally { link.remove(); }
            objectUrls.set(objectUrl, setTimeout(() => {
                URL.revokeObjectURL(objectUrl);
                objectUrls.delete(objectUrl);
            }, 60_000));
        } catch {
            // Failed/CORS-blocked downloads must never fall back to opening the original URL.
        } finally {
            pending.delete(url.href);
        }
    };

    const onClick = (event: MouseEvent) => {
        const target = event.target as Node | null;
        const element = target?.nodeType === 1 ? target as Element : target?.parentElement;
        const link = element?.closest('a[href], area[href]');
        if (!link) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        if (event.type === 'auxclick' || event.button !== 0) return;
        const href = link.getAttribute('href')?.trim();
        if (!href || href.startsWith('#')) return;
        try {
            const base = /^about:/.test(doc.baseURI) ? downloadDocument.baseURI : doc.baseURI;
            const url = new URL(href, base);
            if (['http:', 'https:', 'data:', 'blob:'].includes(url.protocol)) void download(url);
        } catch { /* Invalid links remain inert. */ }
    };
    doc.addEventListener('click', onClick, true);
    doc.addEventListener('auxclick', onClick, true);
    return () => {
        controller.abort();
        doc.removeEventListener('click', onClick, true);
        doc.removeEventListener('auxclick', onClick, true);
        objectUrls.forEach((timer, url) => { clearTimeout(timer); URL.revokeObjectURL(url); });
        objectUrls.clear();
    };
}
